/**
 * GrnVoidDialog — capture an optional reason while voiding a posted GRN.
 *
 * Voiding a posted goods receipt is a heavier action than voiding a PO: it
 * reverses the PO's fulfilment (back to confirmed / partially-received) AND
 * voids the draft matching bill it created. We spell that out in the body so
 * the operator understands the blast radius before confirming. The reason is
 * optional per the wire contract but lands on the audit trail.
 *
 * @see packages/api-contract/src/procedures/goods-receipts.ts — voidInput
 */
import { useEffect, useState } from "react";
import { Undo2 } from "lucide-react";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Label,
} from "@flint/ui";

export interface GrnVoidDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `GRN/2026-27/0003` — used in the body copy. */
  grnNumber: string | null;
  /** Whether a draft matching bill was created (drives the warning copy). */
  hasBill: boolean;
  pending?: boolean;
  errorMessage?: string;
  onConfirm: (payload: { reason: string | null }) => void;
}

export function GrnVoidDialog({
  open,
  onOpenChange,
  grnNumber,
  hasBill,
  pending,
  errorMessage,
  onConfirm,
}: GrnVoidDialogProps) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm({ reason: reason.trim() || null });
  };

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <form onSubmit={handleSubmit} noValidate>
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span
                aria-hidden
                className="inline-grid h-9 w-9 shrink-0 place-items-center rounded-md bg-bad-50 text-bad"
              >
                <Undo2 size={16} />
              </span>
              <div className="flex-1">
                <DialogTitle>Void this goods receipt?</DialogTitle>
                <DialogDescription>
                  {grnNumber ?? "This receipt"} will move to <strong>void</strong>. Its purchase
                  order's fulfilment is recalculated from the remaining posted receipts
                  {hasBill ? ", and the draft matching bill it created is voided too" : ""}. The row
                  stays as a historical record — you can't un-void it.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="mt-4 flex flex-col gap-1.5">
            <Label htmlFor="grn-void-reason">Reason (optional)</Label>
            <textarea
              id="grn-void-reason"
              autoFocus
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder="e.g. Recorded against the wrong purchase order."
              className="min-h-[72px] w-full resize-y rounded-sm border border-line bg-paper px-3 py-2 text-sm leading-relaxed text-ink outline-none transition-colors placeholder:text-muted hover:border-line-2 focus-visible:border-sky-600 focus-visible:ring-2 focus-visible:ring-sky-100"
            />
            <p className="text-sm text-muted">Recorded on the audit trail next to the void.</p>
            {errorMessage ? <p className="mt-1 text-sm text-bad">{errorMessage}</p> : null}
          </div>

          <div className="mt-5 flex items-center justify-end gap-2">
            <Button type="button" intent="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
              Keep receipt
            </Button>
            <Button type="submit" intent="danger" disabled={pending}>
              {pending ? "Voiding…" : "Void receipt"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </DialogRoot>
  );
}
